import type { ApiErrorBody, ErrorCode } from '@bravo/shared';

const BASE = '/api/v1';

export type ApiErrorCode = ErrorCode | 'NETWORK' | 'UNKNOWN';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: ApiErrorCode, status: number, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

type QueryValue = string | number | boolean | undefined | null;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return `${BASE}${path}${qs ? `?${qs}` : ''}`;
}

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'bravo-admin',
  };
  let body: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }
  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body } : {}),
    });
  } catch {
    throw new ApiError('NETWORK', 0, 'Network request failed');
  }
}

async function read<T>(response: Response): Promise<T> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  if (!response.ok) {
    const error = (payload as Partial<ApiErrorBody> | undefined)?.error;
    throw new ApiError(
      (error?.code as ApiErrorCode | undefined) ?? 'UNKNOWN',
      response.status,
      error?.message ?? `Request failed (${response.status})`,
      error?.details,
    );
  }
  return payload as T;
}

export async function apiData<T>(path: string, options?: RequestOptions): Promise<T> {
  const payload = await read<{ data: T }>(await request(path, options));
  return payload.data;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** Cursor lists are `{ data, nextCursor }` at the top level, not wrapped again. */
export async function apiPage<T>(
  path: string,
  query?: Record<string, QueryValue>,
): Promise<Page<T>> {
  const payload = await read<{ data: T[]; nextCursor: string | null }>(
    await request(path, { query }),
  );
  return { items: payload.data, nextCursor: payload.nextCursor };
}
